import React, { useState } from 'react'

import { Container, LayerParsed } from './Layers.styles'

import { Layer, LayerId } from 'modules/shares/types'
import { InspectorState } from 'modules/shares/Inspector/types'

import {
  hasParentGroupWithTints,
  hasParentGroupWithExports,
  hasParentGroupWithStyles,
} from './Layers.utils'
import { scaleFrame, isMac } from '@sketch/utils'

// Layer types that shouldn't appear in the inspector
const SKIP_LAYER_TYPES = ['MSImmutableHotspotLayer']

/**
 * TYPES
 */
interface LayersProps {
  zoomFactor: number
  inspector: InspectorState
}

interface LayersParsedProps {
  layers?: Record<string, Layer>
  zoomFactor: number
  skippingGroups: boolean
}

/**
 * HELPER COMPONENTS
 */
const LayersParsed = ({
  layers = {},
  zoomFactor,
  skippingGroups,
}: LayersParsedProps) => (
  <>
    {Object.values(layers)
      // Scale the frame depending on the current zoom
      .map(({ frame, ...rest }) => ({
        frame: scaleFrame(zoomFactor, frame),
        ...rest,
      }))
      // Sort layers depending on the Layer `order` property, have in mind the
      // layers are returned in reverse order, ready to render them, but for
      // any layer calculation within the same level (same parent) a reverse
      // will be needed
      .sort((a, b) => ((a.order as number) || 0) - ((b.order as number) || 0))
      /**
       * We want to avoid rendering some layers:
       * - A layer that is part of a combined shape (shapeGroup)
       * - Some types of layers, defined in SKIP_LAYER_TYPES
       * - Locked layers
       */
      .filter(
        ({ isCombined, type, isLocked }) =>
          !isCombined && !SKIP_LAYER_TYPES.includes(type) && !isLocked
      )
      /**
       * Loop through every layer mainly doing 2 things:
       * - Define if the layer is clickable or not
       * - Render the layer with absolute position
       */
      .map((layer: Layer) => {
        /**
         * We render symbols instances detached, so there are some situations
         * where we want to make a layer unclickable:
         * - when the layer is a group layer (a symbol instance is just a group
         *   layer) and ⌘ key is pressed
         * - when a layer has a parent group layer (a symbol instance) that has
         *   a Tint (because we want to select the Tint layer instead)
         * - when a layer has a parent group layer (symbol instance) that has
         *   exports (because we want to select the export layer instead)
         * - when a layer belongs to a symbol instance (a group layer) and this
         *   symbol instance is styled (because we want to select the symbol
         *   instance instead)
         * - when a layer has a mask sibling and a parent group that has
         *   noexports, no enabled style options or any tint applied (because
         *   we want to select the mask instead of the bitmap)
         *
         * Note: we are identifying symbol instances checking the `layer.type`
         * in hasParentGroupWith* functions, but it should be easier and less
         * error prone if we just check `isSymbolRoot`.
         */
        const isGroupAndCommandKeyIsPressed =
          layer.type === 'group' && skippingGroups
        const isLayerWithParentGroupWithTints =
          layer.type !== 'text' && hasParentGroupWithTints(layers, layer)
        const isLayerWithParentGroupWithExports =
          layer.type !== 'text' && hasParentGroupWithExports(layers, layer)
        const isLayerWithParentGroupWithStyles =
          layer.type !== 'text' && hasParentGroupWithStyles(layers, layer)

        const isMaskedLayer =
          !!layer.maskedBy &&
          !isLayerWithParentGroupWithTints &&
          !isLayerWithParentGroupWithExports &&
          !isLayerWithParentGroupWithStyles

        const isLayerUnclickable =
          isGroupAndCommandKeyIsPressed ||
          isLayerWithParentGroupWithTints ||
          isLayerWithParentGroupWithExports ||
          isLayerWithParentGroupWithStyles ||
          isMaskedLayer

        // Generate an absolute positioned div for each selectable layer
        return (
          <LayerParsed
            key={layer.id as string}
            isLayerUnclickable={isLayerUnclickable}
            data-name={layer.name}
            data-id={layer.id}
            data-testid={layer.id}
            frame={layer.frame}
          />
        )
      })}
  </>
)

const MemoizedLayersParsed = React.memo(LayersParsed)

/**
 * MAIN COMPONENT
 * Renders layers in the inspector canvas where a user can hover and click
 */
const Layers: React.FC<LayersProps> = ({
  zoomFactor,
  inspector: { onHoverLayer, onClickLayer, data: inspectorData },
  ...rest
}) => {
  const [skippingGroups, setSkippingGroups] = useState<boolean>(false)

  const layerIdFromTarget = (target: HTMLElement) =>
    target?.dataset?.id as LayerId

  // Holding ⌘ (mac) or ⌃ (win) while the user is moving around the inspector
  // will make the groups unclickable
  const handleMouseMove = (event: React.MouseEvent): void => {
    const isCommandKeyPressed = isMac() ? event.metaKey : event.ctrlKey

    if (isCommandKeyPressed !== skippingGroups) {
      setSkippingGroups(isCommandKeyPressed)
    }

    onHoverLayer(layerIdFromTarget(event.target as HTMLElement))
  }

  const handleMouseLeave = (): void => {
    onHoverLayer(undefined)
  }

  const handleTouchMove = (event: React.TouchEvent): void => {
    const myLocation = event.changedTouches[0]
    const realTarget = document.elementFromPoint(
      myLocation.clientX,
      myLocation.clientY
    ) as HTMLElement

    onHoverLayer(layerIdFromTarget(realTarget))
  }

  const handleTouchEnd = (): void => {
    onHoverLayer(undefined)
  }

  const handleClick = (event: React.MouseEvent): void => {
    event.stopPropagation()
    onClickLayer?.(layerIdFromTarget(event.target as HTMLElement))
  }

  return (
    <Container
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      {...rest}
    >
      <MemoizedLayersParsed
        layers={inspectorData?.layers}
        zoomFactor={zoomFactor}
        skippingGroups={skippingGroups}
      />
    </Container>
  )
}

export default Layers
